import React from "react";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { QueryKey, queryCache, useMutation } from "react-query";

import FlagDialog from "components/FlagDialog";
import UncheckButton from "components/UncheckButton";
import { IconButton } from "fe-shared/src/components";
import {
  FlagIcon,
  EditIcon,
  WarningIcon,
} from "fe-shared/src/components/Icons";

import useDialog from "fe-shared/src/hooks/useDialog";

import {
  TransactionFlag,
  TransactionSection,
  Transaction,
  TransactionType,
} from "fe-shared-transactions/src/types";
import { ApiError } from "fe-shared/src/types/common";

import * as api from "routes/TransactionDetails/modules/api";
import { WARNING_COLOR } from "../../consts";

import styles from "./SectionAction.module.scss";

interface Props {
  editCallback?: () => void;
  flags: TransactionFlag[];
  sectionCode: TransactionSection;
  transactionId: number;
  transactionQueryKey: QueryKey;
  transactionNumber: string;
  activitiesQueryKey: QueryKey;
  transactionType: TransactionType;
  canFlagManually?: boolean;
  canEditManualFlag?: boolean;
  canUnflag?: boolean;
}

const SectionAction = ({
  editCallback,
  flags,
  sectionCode,
  transactionId,
  transactionQueryKey,
  transactionNumber,
  activitiesQueryKey,
  transactionType,
  canFlagManually,
  canEditManualFlag,
  canUnflag,
}: Props) => {
  const { t } = useTranslation();
  const {
    isDialogActive,
    isDialogRendered,
    openDialog,
    closeDialog,
  } = useDialog();

  const [addFlag, mutationResult] = useMutation<
    void,
    AxiosError<ApiError>,
    api.ActionFlagParams
  >(api.flagTransaction, {
    onSuccess: (res, params) => {
      closeDialog();
      queryCache.invalidateQueries(activitiesQueryKey);
      queryCache.setQueryData(transactionQueryKey, (prevData?: Transaction) => {
        if (prevData) {
          return {
            ...prevData,
            transactionFlags: [
              ...prevData.transactionFlags,
              {
                isManual: true,
                ...params,
              },
            ],
          };
        }
        return prevData!;
      });
    },
  });

  const [removeFlag] = useMutation<
    void,
    AxiosError<ApiError>,
    api.ActionUnFlagParams
  >(api.unflagTransaction, {
    onSuccess: (res, params) => {
      closeDialog();
      queryCache.invalidateQueries(activitiesQueryKey);
      queryCache.setQueryData(transactionQueryKey, (prevData?: Transaction) => {
        if (prevData) {
          return {
            ...prevData,
            transactionFlags: prevData.transactionFlags.filter(
              (f) => !f.isManual || f.sectionCode !== params.sectionCode
            ),
          };
        }
        return prevData!;
      });
    },
  });

  const manualFlags = flags.filter((flag) => flag.isManual);
  const systemFlags = flags.filter((flag) => !flag.isManual);

  return (
    <>
      <div className={styles.transactionActions}>
        {canEditManualFlag && editCallback && (
          <IconButton
            hasTooltip
            onClick={editCallback}
            id="edit-flag-comment-button"
            aria-label={t("transactions.actions.edit")}
          >
            <EditIcon fillColor={null} />
          </IconButton>
        )}
        {!!systemFlags.length && <WarningIcon fillColor={WARNING_COLOR} />}
        {!!manualFlags.length && canUnflag ? (
          <UncheckButton
            onClick={() => {
              removeFlag({
                transactionId,
                sectionCode,
              });
            }}
            label="flagged"
            icon={FlagIcon}
            disabled={false}
            undoLabel="unflag"
            className={styles.unflag}
          />
        ) : (
          canFlagManually && (
            <IconButton
              hasTooltip
              onClick={openDialog}
              id="flag-comment-button"
              aria-label={t("transactions.actions.addFlag")}
            >
              <FlagIcon fillColor={null} />
            </IconButton>
          )
        )}
      </div>
      {isDialogRendered && (
        <FlagDialog
          transactionType={transactionType}
          transactionId={transactionId}
          sectionCode={sectionCode}
          isActive={isDialogActive}
          onClose={closeDialog}
          onSubmit={addFlag}
          mutationResult={mutationResult}
          transactionNumber={transactionNumber}
        />
      )}
    </>
  );
};

export default SectionAction;
